// ABOUTME: Hook fetching admin-managed header categories and items.
// ABOUTME: Returns nested structure: top-level items + dropdown categories with their items.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface HeaderItem {
  id: string;
  category_id: string | null;
  label: string;
  label_ru: string | null;
  label_el: string | null;
  target_type: 'route' | 'page' | string;
  target_value: string;
  sort_order: number;
  auth_required: boolean;
  hide_when_authed: boolean;
  requires_setting: string | null;
}

export interface HeaderCategory {
  id: string;
  key: string;
  label: string;
  label_ru: string | null;
  label_el: string | null;
  sort_order: number;
  auth_required: boolean;
  requires_setting: string | null;
  items: HeaderItem[];
}

export interface HeaderConfig {
  /** Categories sorted by sort_order, each with sorted items */
  categories: HeaderCategory[];
  /** Items not assigned to a category (top-level), sorted by sort_order */
  topLevel: HeaderItem[];
}

const EMPTY: HeaderConfig = { categories: [], topLevel: [] };

const fetchHeaderConfig = async (): Promise<HeaderConfig> => {
  const [catsRes, itemsRes] = await Promise.all([
    supabase.from('header_categories' as any).select('*').order('sort_order'),
    supabase.from('header_items' as any).select('*').order('sort_order'),
  ]);

  const cats = ((catsRes.data as any[]) || []) as Omit<HeaderCategory, 'items'>[];
  const items = ((itemsRes.data as any[]) || []) as HeaderItem[];

  const categories: HeaderCategory[] = cats.map((c) => ({
    ...c,
    items: items.filter((i) => i.category_id === c.id),
  }));
  const topLevel = items.filter((i) => !i.category_id);

  return { categories, topLevel };
};

export const useHeaderConfig = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['header-config'],
    queryFn: fetchHeaderConfig,
    staleTime: 5 * 60 * 1000,
  });
  return { config: data ?? EMPTY, loading: isLoading };
};

/** Pick the localized label for the current i18n language. */
export const localizedLabel = (
  item: { label: string; label_ru: string | null; label_el: string | null },
  lang: string,
): string => {
  if (lang.startsWith('ru') && item.label_ru) return item.label_ru;
  if (lang.startsWith('el') && item.label_el) return item.label_el;
  return item.label;
};
